import { useState, useEffect } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { consola } from "consola";
import moment from "moment";
import { useRecoilValue } from "recoil";
import organizationIdState from "../atoms/organizationIdSelector";

/**
 * Hook to fetch and process supplement history data for a client
 *
 * @param {Object} params - Parameters for the hook
 * @param {string} params.clientId - The client's ID
 * @param {number} params.days - Number of days to look back for history (default: 30)
 * @returns {Object} - Object containing compliance data and loading state
 */
const useSupplementHistory = ({ clientId, days = 30 }) => {
  const organizationId = useRecoilValue(organizationIdState);
  const [loading, setLoading] = useState(true);
  const [supplementHistory, setSupplementHistory] = useState([]);
  const [supplementCompliance, setSupplementCompliance] = useState({});
  const [overallCompliance, setOverallCompliance] = useState(0);

  useEffect(() => {
    if (!clientId || !organizationId) {
      setLoading(false);
      return;
    }

    const fetchSupplementHistory = async () => {
      try {
        setLoading(true);

        // Calculate date range for the query
        const startDate = moment().subtract(days, "days").startOf("day");
        const endDate = moment().endOf("day");

        // Get all history documents for the date range
        const historyRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("clients")
          .doc(clientId)
          .collection("supplementHistory");

        // Get all dates within the range
        const querySnapshot = await historyRef
          .where("createdOn", ">=", startDate.toDate())
          .where("createdOn", "<=", endDate.toDate())
          .orderBy("createdOn", "desc")
          .get();

        consola.debug(
          "++++++ READ -> useSupplementHistory: fetchSupplementHistory",
        );

        // Process the supplement history data
        const historyData = [];
        querySnapshot.forEach((doc) => {
          // Each doc is a date entry with a history array
          const dateData = doc.data();
          if (dateData.history && Array.isArray(dateData.history)) {
            historyData.push(...dateData.history);
          }
        });

        setSupplementHistory(historyData);

        // Calculate compliance for each supplement
        const supplementMap = {};

        // Group history entries by supplementId
        historyData.forEach((entry) => {
          if (entry.supplementId) {
            if (!supplementMap[entry.supplementId]) {
              supplementMap[entry.supplementId] = {
                id: entry.supplementId,
                name: entry.supplementName || "Unknown Supplement",
                total: 0,
                taken: 0,
                compliance: 0,
                isActive: entry.isActive,
              };
            }

            supplementMap[entry.supplementId].total += 1;
            if (entry.taken) {
              supplementMap[entry.supplementId].taken += 1;
            }
          }
        });

        // Calculate compliance percentage for each supplement
        const compliance = {};
        let totalCompliance = 0;
        let totalSupplements = 0;

        Object.keys(supplementMap).forEach((id) => {
          const supplement = supplementMap[id];
          const complianceRate =
            supplement.total > 0
              ? Math.round((supplement.taken / supplement.total) * 100)
              : 0;

          compliance[id] = {
            ...supplement,
            compliance: complianceRate,
          };

          totalCompliance += complianceRate;
          totalSupplements++;
        });

        // Calculate overall compliance
        const calculatedOverallCompliance =
          totalSupplements > 0
            ? Math.round(totalCompliance / totalSupplements)
            : 0;

        setSupplementCompliance(compliance);
        setOverallCompliance(calculatedOverallCompliance);
        setLoading(false);
      } catch (error) {
        consola.error("Error fetching supplement history:", error);
        setLoading(false);
      }
    };

    fetchSupplementHistory();
  }, [clientId, organizationId, days]);

  /**
   * Updates the supplement compliance data for a specific client
   * @param {Object} client - The client object
   * @param {Object} newSupplement - The new supplement object
   */
  const updateSupplementCompliance = (client, supplementData) => {
    if (!client || !supplementData) return;

    // Update the client's supplement data with the calculated compliance
    if (supplementCompliance[supplementData.supplementId]) {
      supplementData.compliance =
        supplementCompliance[supplementData.supplementId].compliance;
    }
  };

  return {
    loading,
    supplementHistory,
    supplementCompliance,
    overallCompliance,
    updateSupplementCompliance,
  };
};

export default useSupplementHistory;
